import { Button } from '@/common/ui/button'
import { Checkbox } from '@/common/ui/checkbox'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'
import { Input } from '@/common/ui/input'
import { Filter } from 'lucide-react'
import { useState, useMemo } from 'react'

interface DataTableMultiCheckboxFilterProps {
	options: {
		label: string
		value: string
	}[]
	selectedValues: string[]
	onSelectionChange: (values: string[]) => void
	placeholder?: string
	className?: string
}

export function DataTableMultiCheckboxFilter({
	options,
	selectedValues,
	onSelectionChange,
	placeholder = 'เลือกตัวกรอง',
	className,
}: DataTableMultiCheckboxFilterProps) {
	const [isOpen, setIsOpen] = useState(false)
	const [searchQuery, setSearchQuery] = useState('')

	const filteredOptions = useMemo(() => {
		if (!searchQuery) return options
		return options.filter((option) =>
			option.label.toLowerCase().includes(searchQuery.toLowerCase()),
		)
	}, [options, searchQuery])

	const toggleValue = (value: string) => {
		if (selectedValues.includes(value)) {
			onSelectionChange(selectedValues.filter((v) => v !== value))
		} else {
			onSelectionChange([...selectedValues, value])
		}
	}

	const toggleAll = (checked: boolean) => {
		if (checked) {
			onSelectionChange(options.map((option) => option.value))
		} else {
			onSelectionChange([])
		}
	}

	const allSelected = selectedValues.length === options.length
	const someSelected = selectedValues.length > 0 && !allSelected

	return (
		<Popover open={isOpen} onOpenChange={setIsOpen}>
			<PopoverTrigger asChild>
				<Button variant="outline" className={className}>
					<Filter className="mr-2 h-4 w-4" />
					{placeholder}
					{selectedValues.length > 0 && (
						<span className="ml-2 rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">
							{selectedValues.length}
						</span>
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-[300px] p-0" align="start">
				<div className="border-b p-3">
					<div className="flex items-center space-x-2">
						<Checkbox
							id="select-all"
							checked={allSelected}
							ref={(node) => {
								if (node) {
									;(node as any).indeterminate = someSelected
								}
							}}
							onCheckedChange={toggleAll}
						/>
						<label
							htmlFor="select-all"
							className="cursor-pointer text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
						>
							{allSelected
								? 'ยกเลิกเลือกทั้งหมด'
								: someSelected
									? 'เลือกทั้งหมด'
									: 'เลือกทั้งหมด'}
						</label>
					</div>
				</div>
				<div className="border-b p-2">
					<Input
						placeholder="ค้นหา..."
						className="h-8"
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
					/>
				</div>
				<div className="max-h-[300px] overflow-y-auto p-3">
					<div className="space-y-3">
						{filteredOptions.length > 0 ? (
							filteredOptions.map((option) => (
								<div key={option.value} className="flex items-center space-x-2">
									<Checkbox
										id={option.value}
										checked={selectedValues.includes(option.value)}
										onCheckedChange={() => toggleValue(option.value)}
									/>
									<label
										htmlFor={option.value}
										className="cursor-pointer text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
									>
										{option.label}
									</label>
								</div>
							))
						) : (
							<div className="py-6 text-center text-sm text-muted-foreground">
								ไม่พบผลลัพธ์
							</div>
						)}
					</div>
				</div>
			</PopoverContent>
		</Popover>
	)
}
